import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Eye, Trash2, Cable } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { CableEdge } from '../../hooks/useCablesList';
import toast from 'react-hot-toast';

interface CablesTableProps {
  dcCode: string;
  filteredEdges: CableEdge[];
  canEdit: boolean;
  deletingId: string | null;
  handleDelete: (id: string) => void;
}

export const CablesTable: React.FC<CablesTableProps> = ({
  dcCode,
  filteredEdges,
  canEdit,
  deletingId,
  handleDelete
}) => {
  const navigate = useNavigate();
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: filteredEdges.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 10,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0]?.start || 0 : 0;
  const paddingBottom = virtualItems.length > 0
    ? rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end || 0)
    : 0;

  const copyToClipboard = (id: string) => {
    navigator.clipboard.writeText(id);
    toast.success(`ID кабеля скопирован в буфер обмена: ${id.substring(0, 8)}...`);
  };

  if (filteredEdges.length === 0) {
    return (
      <div className="text-center py-20">
        <Cable size={32} className="text-slate-600 mx-auto mb-2" />
        <p className="text-gray-400 text-sm">Соединения не найдены</p>
      </div>
    );
  }

  return (
    <div ref={parentRef} className="overflow-auto h-[600px] w-full scrollbar-thin">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-slate-800/80 bg-slate-900/90 backdrop-blur-md text-left">
            <th className="px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider">Кабель ID</th>
            <th className="px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider text-center">Источник</th>
            <th className="px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider">Устройство-Источник (А)</th>
            <th className="px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider">Порт А</th>
            <th className="px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider">Устройство-Цель (Б)</th>
            <th className="px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider">Порт Б</th>
            <th className="px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider">Тип / Цвет</th>
            <th className="px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider text-right">Длина</th>
            <th className="px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider text-center">Статус</th>
            <th className="px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider text-center">Действия</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/30">
          {paddingTop > 0 && (
            <tr>
              <td style={{ height: `${paddingTop}px` }} colSpan={10} />
            </tr>
          )}
          {virtualItems.map((virtualRow) => {
            const edge = filteredEdges[virtualRow.index];
            return (
              <tr 
                key={edge.cable_id} 
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                className="hover:bg-slate-800/25 transition-colors text-sm border-slate-800/20"
              >
                {/* Cable ID Copy pill */}
                <td className="px-4 py-3 font-mono text-xs text-text-muted">
                  <button
                    title="Скопировать полный ID кабеля"
                    onClick={() => copyToClipboard(edge.cable_id)}
                    className="px-2 py-0.5 bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800/50 rounded-xs text-[10px] font-mono text-text-secondary hover:text-text-primary transition-all duration-200 select-all cursor-pointer flex items-center gap-1.5"
                  >
                    <span>{edge.cable_id.substring(0, 8)}</span>
                    <span className="text-[9px] opacity-60">📋</span>
                  </button>
                </td>
                {/* Source Column (NetBox vs Manual) */}
                <td className="px-4 py-3 text-center">
                  {edge.netbox_id ? (
                    <span
                      data-testid="badge-cable-netbox"
                      className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-cyan-950/60 text-cyan-300 border border-cyan-800/80 inline-flex items-center gap-1"
                      title={`Импортировано из NetBox (ID: ${edge.netbox_id})`}
                    >
                      NetBox #{edge.netbox_id}
                    </span>
                  ) : (
                    <span
                      data-testid="badge-cable-manual"
                      className="px-2 py-0.5 rounded-full text-[10px] font-mono text-slate-400 bg-slate-900/60 border border-slate-800 inline-flex items-center gap-1"
                      title="Создано вручную в DC Cable Link"
                    >
                      Ручной
                    </span>
                  )}
                </td>
                {/* Clickable Source Device Link */}
                <td className="px-4 py-3 font-medium text-text-primary">
                  <Link 
                    to={`/${dcCode}/equipment/${edge.source_device_id || edge.source_device}`}
                    className="text-text-primary hover:text-accent hover:underline transition-colors font-semibold"
                  >
                    {edge.source_device}
                  </Link>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-accent">
                  {edge.source_port}
                </td>
                {/* Clickable Target Device Link */}
                <td className="px-4 py-3 font-medium text-text-primary">
                  <Link 
                    to={`/${dcCode}/equipment/${edge.target_device_id || edge.target_device}`}
                    className="text-text-primary hover:text-accent hover:underline transition-colors font-semibold"
                  >
                    {edge.target_device}
                  </Link>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-accent">
                  {edge.target_port}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-2.5 h-2.5 rounded-full border border-black/40 flex-shrink-0"
                      style={{ backgroundColor: edge.color }} 
                    />
                    <span>{edge.cable_type}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs">
                  {edge.length_m !== null ? `${edge.length_m.toFixed(1)} м` : '—'}
                </td>
                {/* Dynamic high contrast status pills */}
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                    edge.status.toLowerCase() === 'live'
                      ? 'text-green-700 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-500/10 dark:border-green-500/30'
                      : edge.status.toLowerCase() === 'planned'
                      ? 'text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-500/10 dark:border-blue-500/30'
                      : 'text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-500/10 dark:border-red-500/30'
                  }`}>
                    {edge.status}
                  </span>
                </td>
                {/* Interactive action buttons */}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      title="Трассировать (Trace) на карте топологии"
                      onClick={() => navigate(`/${dcCode}/topology?cable=${edge.cable_id}`)}
                      className="p-1.5 hover:bg-slate-900/60 border border-slate-800/40 text-text-secondary hover:text-accent rounded-sm transition-all duration-200 hover:scale-105 active:scale-95"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      title={canEdit ? "Расторгнуть соединение" : "Удаление кабелей доступно только для инженеров"}
                      disabled={!canEdit || deletingId === edge.cable_id}
                      onClick={() => handleDelete(edge.cable_id)}
                      className="p-1.5 hover:bg-slate-900/60 border border-slate-800/40 text-text-secondary hover:text-status-critical rounded-sm transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-40"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {paddingBottom > 0 && (
            <tr>
              <td style={{ height: `${paddingBottom}px` }} colSpan={10} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
